import type { Pipeline as PipelineType } from 'app/generated/graphql'
import type { PipelineCatalogEntry, PipelineStageInfo } from './usePipelinesCatalog'

/**
 * A system pipeline's stage. `exit` is ours: it means a card moved here leaves
 * its board, because the queue's own search stops matching the contact. The
 * API's stages have no such idea, so it is not on `PipelineStageInfo` itself.
 */
type SystemStage = PipelineStageInfo & { exit?: boolean }

/**
 * Where a contact lands when the queue puts them on a board and nobody has
 * worked them yet.
 *
 * A real stage on every system pipeline, because a membership cannot be
 * written without one (`ContactPipelineInput.stage` is required) and because
 * a board that shows somebody in a pipeline while their record says otherwise
 * is a board telling a story the data will not back up.
 */
export const NOT_STARTED_STAGE = `Not started`

/**
 * The stage that means a patient is booked in.
 *
 * The same word on all three system pipelines on purpose: it is the outcome
 * every one of these queues exists to reach, so anything reacting to it can
 * ask one question rather than three.
 */
export const SCHEDULED_STAGE = `Scheduled`

type SystemPipelineEntry = Omit<PipelineCatalogEntry, 'stages'> & {
  stages: SystemStage[]
  /**
   * Whether a contact's place in this pipeline is a `pipelines` membership.
   *
   * True for Hygiene Recall and Unscheduled treatment, whose boards write one.
   * False for Active Leads, whose board reads and writes `leadStatus` instead,
   * so no contact has ever held a `system:leads` membership and none ever
   * should.
   *
   * It decides whether the pipeline is offered anywhere that searches the
   * `pipelines` array. A filter for a pipeline nothing writes to is a filter
   * that always answers zero.
   */
  membershipBacked: boolean
}

/**
 * Pipelines the product owns rather than the practice.
 *
 * These are constants, not records. The precedent is the Active Leads contact
 * list: it exists in every workspace and nobody can delete it because it is a
 * `case` in `useContactsPage` rather than a row in a table. User-made lists are
 * `customPages` records sitting alongside it.
 *
 * That works here because nothing on the server validates a membership's
 * pipeline id, and because a stage move is an `updateContact` on the whole
 * `pipelines` array rather than a call that has to resolve the pipeline. Proven
 * against dev on 2026-08-24: a membership written with a reserved id persists,
 * moves between stages, and comes back with the same derived `key` a real
 * pipeline would produce.
 *
 * A practice that already built its own recall pipeline keeps it untouched.
 * These are provided in addition, never adopted or renamed.
 */
export const SYSTEM_PIPELINES: SystemPipelineEntry[] = [
  {
    id: `system:hygiene`,
    membershipBacked: true,
    name: `Hygiene Recall`,
    // Reached out and Responded are two different facts: one is what the
    // practice did, the other is what the patient did back. An earlier draft
    // collapsed both into `SMS Sent`, which named the channel rather than the
    // step and left nowhere to record that a call went unanswered.
    //
    // The last three are outcomes rather than places a card rests, marked
    // `exit` below: booking or completing a visit takes the patient out of the
    // hygiene queue's own search whether the board likes it or not, and Lost
    // is the manual equivalent.
    stages: [
      // A real stage, not a synthetic column. Everyone the queue finds is
      // enrolled here, so the contact record says what the board says: in
      // Hygiene Recall, nobody has picked them up yet.
      { name: NOT_STARTED_STAGE, order: 0, color: `pipelines-gray` },
      { name: `Started`, order: 1, color: `pipelines-silver` },
      { name: `Reached out`, order: 2, color: `pipelines-blue` },
      { name: `Responded`, order: 3, color: `pipelines-amber` },
      { name: SCHEDULED_STAGE, order: 4, color: `pipelines-green`, exit: true },
      { name: `Lost`, order: 5, color: `pipelines-salmon`, exit: true },
      { name: `Completed`, order: 6, color: `pipelines-green`, exit: true },
    ],
  },
  {
    id: `system:treatment`,
    membershipBacked: true,
    name: `Unscheduled treatment`,
    // Unchanged from the board's own four working stages, so nothing it drew
    // has to move. `Scheduled` and `Lost` are new: they used to be
    // separate actions (`markScheduled`, `decline`) backed by a local list, on
    // the reasoning that treatment's exits were not places a card rests. That
    // reasoning was the bug: a local list cannot survive a refresh. Now
    // `markScheduled`/`decline` write these two stages the same way `move`
    // writes any other, and `exit: true` is what takes the card off the
    // board's rendered columns once they do (see `TreatmentBoardComponent.tsx`
    // for the receipt strip that still shows them, sourced from the same
    // membership rather than from local state).
    stages: [
      // No separate Not started here: "Not contacted" already IS this
      // pipeline's first stage, and it means the same thing. Enrolment lands
      // on whichever stage a board draws first, not on a fixed name.
      { name: `Not contacted`, order: 0, color: `pipelines-silver` },
      { name: `Reached out`, order: 1, color: `pipelines-blue` },
      { name: `In conversation`, order: 2, color: `pipelines-amber` },
      { name: `Ready to schedule`, order: 3, color: `pipelines-green` },
      { name: SCHEDULED_STAGE, order: 4, color: `pipelines-green`, exit: true },
      { name: `Lost`, order: 5, color: `pipelines-salmon`, exit: true },
    ],
  },
  {
    id: `system:leads`,
    membershipBacked: false,
    name: `Active Leads`,
    // Unlike Hygiene and Treatment, nothing ever writes a `system:leads`
    // membership, and nothing should. The Active Leads board reads and moves
    // a contact's `leadStatus` field directly (`leadStatusSource` in
    // `boardColumnSource.ts`), since it predates pipelines and never adopted
    // them, per that source's queue registry entry (`board: { source:
    // 'lead-status' }` in `queueRegistry.ts`, deliberately not `pipeline`).
    // This entry exists only so Active Leads can be found by id/name/colour
    // wherever a system pipeline can (the Pipelines nav, the pipeline filter,
    // a membership resolver), never so a board can be pointed at
    // `pipelineSource` for it. Doing that would build columns that search a
    // `pipelines` array no contact has ever had one written to, so the board
    // would render exactly as many cards as `system:hygiene`'s stray
    // membership: zero.
    //
    // Stage names are `LEAD_COLUMNS`'s `key` values (`boardColumnSource.ts`),
    // not its display labels, because `leadStatus` stores the key. Written as
    // a literal copy rather than an import of `LEAD_COLUMNS`: that file
    // already imports `systemPipeline` from this one, so importing back would
    // make the two modules circular. `systemPipelines.spec.ts` compares this
    // list against `LEAD_COLUMNS` itself so the two cannot drift unnoticed.
    stages: [
      { name: `Needs Action`, order: 0, color: `pipelines-salmon` },
      { name: `In Progress`, order: 1, color: `pipelines-blue` },
      { name: SCHEDULED_STAGE, order: 2, color: `pipelines-green`, exit: true },
      { name: `Closed Lost`, order: 3, color: `pipelines-silver`, exit: true },
    ],
  },
]

/**
 * The feature toggle gating the Action Center, and with it these pipelines.
 *
 * They are its queues wearing a pipeline's clothes: its boards are the only
 * thing that writes them and the only thing that can work them. A workspace
 * without the toggle has every `/action-center/*` URL bounced home by
 * `FeatureRoute`, so listing the pipelines anywhere else gives it a nav entry, a
 * filter option and a picker row that all lead somewhere it cannot go — and a
 * system pipeline cannot be deleted either, since the Pipelines page refuses to
 * restructure one.
 *
 * The merge sites read it through `featureEnabled` (outside React) or
 * `useFeatureToggle` (inside), never by re-typing the string.
 */
export const ACTION_CENTER_FLAG = `action-center`

/** Reserved ids are prefixed, and a real pipeline id is 24 character hex. */
export function isSystemPipelineId(id?: string | null): boolean {
  return !!id && id.startsWith(`system:`)
}

export function systemPipeline(id?: string | null): SystemPipelineEntry | undefined {
  return SYSTEM_PIPELINES.find((pipeline) => pipeline.id === id)
}

/**
 * A system pipeline constant reshaped to the strict server type every other
 * pipeline in this store carries. `orgId`/`workspaceId` are meaningless for a
 * constant that belongs to no workspace record; they are empty strings rather
 * than omitted, since every reader that receives a `Pipeline` treats its fields as
 * always present. One consequence worth knowing: `PipelineInfo` and
 * `ContactPipelinesV2` build a link carrying `wId=${pipeline.workspaceId}`, so a
 * system pipeline produces an empty `wId=` rather than an absent one.
 * `WorkspaceGate` reads an empty string as absent and does nothing, which is why
 * empty beats omitted here: `undefined` would stringify into that URL as the
 * literal text `undefined` and drive a real, failing workspace switch.
 */
export function toPipelineType(entry: PipelineCatalogEntry): PipelineType {
  return {
    id: entry.id,
    name: entry.name,
    orgId: ``,
    workspaceId: ``,
    stages: entry.stages.map((stage) => ({ name: stage.name, color: stage.color ?? ``, order: stage.order ?? 0 })),
  }
}

/**
 * The workspace's pipelines with the product's own in front, in the Action
 * Center's own light catalog shape (`PipelineCatalogEntry`: id, name, stages,
 * nothing else). Use this only for the Action Center's own catalog reader
 * (`usePipelinesCatalog`). A reader typed against the generated `Pipeline`
 * shape (a real `orgId`/`workspaceId`, not merely an optional one) wants
 * `mergeSystemPipelinesAsPipelines` below instead; the two no longer differ in
 * order, only in which shape they hand back.
 *
 * System pipelines lead because they are the ones the Action Center's boards
 * read, and because the Pipelines nav sorts them to the top for the same
 * reason: anything the practice made is additional. A record carrying a
 * reserved id is ignored in favour of the constant, since the boards' columns
 * are written against the constant's stages.
 */
export function mergeSystemPipelines(fromApi: PipelineCatalogEntry[]): PipelineCatalogEntry[] {
  const theirs = fromApi.filter((pipeline) => !isSystemPipelineId(pipeline.id))
  return [...SYSTEM_PIPELINES, ...theirs]
}

/**
 * The workspace's pipelines with the product's own in front, adapted through
 * `toPipelineType` to the server's actual `Pipeline` shape. Use this, not
 * `mergeSystemPipelines`, for any reader typed against the generated model
 * (the sidebar's list, `PipelineStore.fetchList`, a filter dropdown sourced
 * from the same `pipelines` query).
 *
 * The only thing left distinguishing this from `mergeSystemPipelines` is that
 * return shape: `mergeSystemPipelines`' looser `PipelineCatalogEntry` would
 * let a system pipeline's missing `orgId`/`workspaceId` slip through
 * structurally instead of getting the same explicit `` a real `Pipeline`
 * reader can always rely on. Order used to differ too: this one appended
 * because `usePipelinesPage` defaulted to `pipelines[0]` as its active tab,
 * and leading with the constants would have hijacked that default onto a
 * system pipeline for every workspace. That consumer now names what it wants
 * explicitly (`pipelines.find((p) => !isSystemPipelineId(p.id)) ??
 * pipelines[0]` in `usePipelinesPage.tsx`), so nothing depends on append order
 * any more and this was flipped to match `mergeSystemPipelines`.
 */
export function mergeSystemPipelinesAsPipelines(fromApi: PipelineType[]): PipelineType[] {
  const theirs = fromApi.filter((pipeline) => !isSystemPipelineId(pipeline.id))
  return [...SYSTEM_PIPELINES.map(toPipelineType), ...theirs]
}

/**
 * True for a pipeline whose membership a `pipelines` search can actually find.
 *
 * Active Leads is a pipeline in the nav and a board on screen, but a contact's
 * place in it is `leadStatus`, not a membership. Offering it in a filter that
 * searches `pipelines.id` or `pipelines.key` gives somebody an option that
 * always answers zero, which reads as "no leads" rather than as "wrong
 * question".
 */
export function isMembershipBacked(pipeline: { id: string }): boolean {
  const system = systemPipeline(pipeline.id)
  // A practice's own pipelines are records with real memberships.
  return system ? system.membershipBacked : true
}

/**
 * Whether the board behind a system pipeline would actually draw this contact.
 *
 * A board renders from its queue's own search, not from membership, so a
 * membership on a contact the search does not match is invisible and nobody can
 * clear it. This answers that question directly, which is what lets a picker
 * offer a system pipeline at all (see `isAssignablePipeline`).
 *
 * Mirrors the queue searches rather than restating a looser rule: `hygieneWindow`
 * in `queueRegistry.ts` for hygiene, `TREATMENT_POPULATION` in
 * `treatmentBands.ts` for treatment. `hygieneQueueAdmits` is exported so a spec
 * can pin the two together.
 *
 * A pipeline with no queue behind it (`membershipBacked: false`, i.e.
 * `system:leads`, whose board moves `leadStatus` rather than a membership) is
 * never admitted: writing a membership there would do nothing at all.
 */
export function systemQueueAdmits(
  pipelineId: string | null | undefined,
  contact: SystemQueueContact,
  now: Date = new Date(),
): boolean {
  const system = systemPipeline(pipelineId)
  if (!system) return true
  if (!system.membershipBacked) return false
  if (system.id === `system:treatment`) return (contact.unbookedTreatmentValue ?? 0) >= 1
  if (system.id === `system:hygiene`) return hygieneQueueAdmits(contact, now)
  return false
}

/** The contact fields the system queues search on. Structural so callers can pass a `ContactJson`. */
export type SystemQueueContact = {
  patientStatus?: string | null
  appointmentStatus?: string | null
  hygieneDueDate?: string | null
  nearestAppointmentDate?: string | null
  birthDate?: string | null
  isPerio?: boolean | null
  unbookedTreatmentValue?: number | null
}

const HYGIENE_EXCLUDED_PATIENT_STATUSES = [`NonPatient`, `Inactive`, `Archived`]
/**
 * The window the hygiene queues cover between them, in days either side of today.
 *
 * Both bounds, not just the lookahead. The three bands are `hygieneWindow(-3650, 0)`,
 * `(1, 30)` and `(-3650, -31)`, so a recall older than ten years falls off the back
 * of every one of them. Guarding only the upper bound would admit such a contact to
 * a board that could never draw them, which is the whole failure this predicate exists
 * to prevent.
 */
const HYGIENE_LOOKBEHIND_DAYS = 3650
const HYGIENE_LOOKAHEAD_DAYS = 30
const ADULT_YEARS = 18
/** Statuses that mean the future appointment on the record is not a chair they still hold. */
const LAPSED_APPOINTMENT_STATUSES = [`Broken`, `Cancelled`]

/**
 * A date as a whole-day index in UTC.
 *
 * The queue searches compare against `YYYY-MM-DD` strings built with `moment.utc()`,
 * and a date-only bound covers the entire day. Comparing full timestamps here would
 * disagree with them for part of every day: an appointment at 09:00 today is before
 * `new Date()` at 14:00, so a timestamp comparison calls it past while the board's
 * `[today TO *]` still excludes it. Whole days on both sides removes the window.
 */
const utcDayIndex = (date: Date): number =>
  Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) / 86_400_000)

const parseDay = (value: string | null | undefined): number | undefined => {
  if (!value) return undefined
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? undefined : utcDayIndex(parsed)
}

/** The hygiene queue's own rules, in the same order `hygieneWindow` states them. */
export function hygieneQueueAdmits(contact: SystemQueueContact, now: Date = new Date()): boolean {
  if (contact.patientStatus && HYGIENE_EXCLUDED_PATIENT_STATUSES.includes(contact.patientStatus)) return false
  if (contact.appointmentStatus === `Scheduled`) return false
  if (contact.isPerio === true) return false

  const today = utcDayIndex(now)
  const due = parseDay(contact.hygieneDueDate)
  if (due === undefined) return false
  if (due < today - HYGIENE_LOOKBEHIND_DAYS || due > today + HYGIENE_LOOKAHEAD_DAYS) return false

  // "18 or older" as the queue asks it: born on or before the day 18 years ago, so
  // somebody whose birthday is today counts.
  const born = parseDay(contact.birthDate)
  if (born === undefined) return false
  const adultBy = new Date(Date.UTC(now.getUTCFullYear() - ADULT_YEARS, now.getUTCMonth(), now.getUTCDate()))
  if (born > utcDayIndex(adultBy)) return false

  // "Nobody who already has a chair", with the same carve-out the queue makes: a
  // future date attached to a broken or cancelled appointment is not a chair.
  if (contact.appointmentStatus && LAPSED_APPOINTMENT_STATUSES.includes(contact.appointmentStatus)) return true
  const nearest = parseDay(contact.nearestAppointmentDate)
  // `[today TO *]` covers all of today, so a same-day appointment is a chair.
  if (nearest !== undefined && nearest >= today) return false
  return true
}

/**
 * True for a pipeline a generic picker (the contacts table's bulk action, the
 * contact drawer's "Add to pipeline" modal, a workflow's Add to Pipeline node)
 * may offer someone to add a contact to.
 *
 * A system pipeline used to be refused outright here, because a board renders
 * from its queue's own search and a membership on a contact that search does not
 * match is invisible and impossible to clear. That reasoning is sound but the
 * remedy was too broad: it left no way at all to put somebody on a system board,
 * including the contacts the queue would happily draw.
 *
 * So the question moved from "is this a system pipeline" to "would its board show
 * this contact". Pass the contact and a system pipeline is offered when its queue
 * admits them; omit it and system pipelines are refused as before, which keeps
 * callers that have no single contact in hand (an options list built before any
 * row is chosen) on the safe side.
 */
export function isAssignablePipeline(
  pipeline: { id?: string | null },
  contact?: SystemQueueContact,
  now: Date = new Date(),
): boolean {
  if (!isSystemPipelineId(pipeline.id)) return true
  return !!contact && systemQueueAdmits(pipeline.id, contact, now)
}
